/* global __initial_auth_token */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Autocomplete,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Typography,
    Chip,
    Container,
} from '@mui/material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

// Dados de exemplo para os componentes
const diasSemana = ['Domingo', 'Segunda-Feira', 'Terça-Feira', 'Quarta-Feira', 'Quinta-Feira', 'Sexta-Feira', 'Sábado'];

// Lógica para determinar o dia da semana seguinte
const getProximoDia = () => {
    const hoje = new Date();
    const proximoDiaIndex = (hoje.getDay() + 1) % 7;
    return diasSemana[proximoDiaIndex];
};

const GerarPlanilhaProducao = () => {
    // Estados
    const [diaSelecionado, setDiaSelecionado] = useState(getProximoDia());
    const [datasPaisHora, setDatasPaisHora] = useState([]);
    const [selectedPaesHora, setSelectedPaesHora] = useState([]);
    const [datasMix, setDatasMix] = useState([]);
    const [selectedMix, setSelectedMix] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openPaesHora, setOpenPaesHora] = useState(false);
    const [openMix, setOpenMix] = useState(false);
    const [tabelaPaisHoraData, setTabelaPaisHoraData] = useState([]);
    const [tabelaMixData, setTabelaMixData] = useState([]);
    const [filtroMinimo, setFiltroMinimo] = useState('');

    // Extrai o número entre parênteses de uma string de data
    const getNumeroParenteses = (dateString) => {
        const match = dateString.match(/\((\d+)\)/);
        return match ? parseInt(match[1]) : 0;
    };

    // Números únicos disponíveis para o filtro (apenas das datas Pães Hora)
    const numerosDisponiveis = [...new Set(
        datasPaisHora.map(getNumeroParenteses)
    )].sort((a, b) => a - b);

    // Lista Pães Hora filtrada com base no filtroMinimo
    const datasPaisHoraFiltradas = filtroMinimo !== ''
        ? datasPaisHora.filter(d => getNumeroParenteses(d) >= Number(filtroMinimo))
        : datasPaisHora;

    // Colunas dinâmicas para as tabelas
    const colunasHora = ['Hora', ...selectedPaesHora.map(d => d.split(' ')[0])];
    const colunasMix = ['Produto', ...selectedMix];
    
    const { authenticatedFetch, logout } = useAuth();

    const handleDiaChange = async (selectedDia) => {
        setDiaSelecionado(selectedDia);
        setLoading(true);
        setError(null);
        setSelectedPaesHora([]);
        setSelectedMix([]);
        setFiltroMinimo('');
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/get_datas_by_dia/?dia_semana=${encodeURIComponent(selectedDia)}`, {
                method: 'GET',
            });

            if (!response.ok) {
                const contentType = response.headers.get("content-type");
                let errorMessage = 'Erro desconhecido. Por favor, tente novamente.';
                
                if (contentType && contentType.includes("application/json")) {
                    const errorData = await response.json();
                    errorMessage = errorData.error || `Erro ao carregar datas. Status: ${response.status}`;
                } else {
                    errorMessage = await response.text();
                    console.error('Resposta não-JSON do servidor:', errorMessage);
                    errorMessage = `Erro no servidor. Resposta recebida: "${errorMessage.slice(0, 50)}..."`;
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();
            
            if (data.date_options && Array.isArray(data.date_options)) {
                setDatasPaisHora(data.date_options);
            } else {
                console.warn('date_options não encontrado ou não é array');
                setDatasPaisHora([]);
            }

            if (data.mix_dates && Array.isArray(data.mix_dates)) {
                setDatasMix(data.mix_dates);
            } else {
                console.warn('mix_dates não encontrado ou não é array');
                setDatasMix([]);
            }

        } catch (error) {
            console.error('Erro detalhado:', error);
            
            if (error.message.includes('HTML') || error.message.includes('DOCTYPE')) {
                setError('A API retornou uma página HTML ao invés de dados JSON. Verifique se o endpoint está correto.');
            } else if (error.message.includes('fetch')) {
                setError('Erro de conexão. Verifique se o servidor está rodando.');
            } else if (error.message.includes('Token')) {
                logout();
            } else {
                alert(`Erro ao buscar dados: ${error.message}`);
            }
            
            setDatasPaisHora([]);
            setDatasMix([]);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        handleDiaChange(diaSelecionado);
    }, []);

    const fetchPaesHoraData = async (selectedDates) => {
        const cleanedDates = selectedDates.map(dateString => dateString.split(' ')[0]);
        if (cleanedDates.length === 0) return;
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/get_paes_hora/?dates=${cleanedDates.join(',')}`, {
                method: 'GET',
            });
            if (!response.ok) {
                throw new Error('Erro ao buscar dados da tabela Paes Hora');
            }
            const data = await response.json();
            setTabelaPaisHoraData(data);
        } catch (err) {
            if (err.message.includes('Token')) {
                logout();
            } else {
                alert('Erro: ' + err.message);
            }
            setTabelaPaisHoraData([]);
        }
    };

    const fetchMixData = async (selectedDates) => {
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/get_mix/?dates=${selectedDates.join(',')}`, {
                method: 'GET',
            });
            if (!response.ok) {
                throw new Error('Erro ao buscar dados da tabela Mix');
            }
            const data = await response.json();
            setTabelaMixData(data);
        } catch (err) {
            if (err.message.includes('Token')) {
                logout();
            } else {
                alert('Erro: ' + err.message);
            }
            setTabelaMixData([]);
        }
    };

    const handleDownloadExcel = async () => {  
        setLoading(true);
        setError(null);
    
        try {
            const payload = {
                dia_semana: diasSemana.indexOf(diaSelecionado),
                paes_hora_data: tabelaPaisHoraData,
                mix_data: tabelaMixData,
                selectedPaesHora: selectedPaesHora,
                selectedMix: selectedMix,
            };
            
            const response = await authenticatedFetch('' + API_BASE_URL + '/api/download_excel/', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
    
            if (!response.ok) {
                throw new Error('Erro ao gerar a planilha Excel. Por favor, tente novamente.');
            }
    
            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = 'download.xlsx';
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fileNameMatch != null && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/['"]/g, '');
                }
            }
    
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            
        } catch (err) {
            if (err.message.includes('Token')) {
                logout();
            } else {
                alert('Erro: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };
    
    // Atualiza a tabela Pães Hora quando as datas selecionadas mudam
    useEffect(() => {
        if (selectedPaesHora.length > 0) {
            fetchPaesHoraData(selectedPaesHora);
        } else {
            setTabelaPaisHoraData([]);
        }
    }, [selectedPaesHora]);

    // Atualiza a tabela Mix quando as datas selecionadas mudam
    useEffect(() => {
        if (selectedMix.length > 0) {
            fetchMixData(selectedMix);
        } else {
            setTabelaMixData([]);
        }
    }, [selectedMix]);

    // Ao mudar o filtro, remove seleções de Pães Hora que não passam mais no filtro
    const handleFiltroChange = (novoFiltro) => {
        setFiltroMinimo(novoFiltro);
        if (novoFiltro !== '') {
            setSelectedPaesHora(prev =>
                prev.filter(d => getNumeroParenteses(d) >= Number(novoFiltro))
            );
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} sx={{ alignItems: 'flex-end', mb: 2 }}>
                    <Grid xs={12} md={4}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Selecione o dia da semana
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={diaSelecionado}
                                onChange={(event) => handleDiaChange(event.target.value)}
                                disabled={loading}
                            >
                                {diasSemana.map((dia) => (
                                    <MenuItem key={dia} value={dia}>
                                        {dia}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid xs={12} md={8}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button 
                                variant="outlined" 
                                disabled={loading || (tabelaPaisHoraData.length === 0 && tabelaMixData.length === 0)}
                                onClick={handleDownloadExcel}
                            >
                                Baixar Excel
                            </Button>

                            {/* Filtro mínimo por número entre parênteses — afeta apenas Pães Hora */}
                            <FormControl
                                size="small"
                                sx={{ minWidth: 160 }}
                                disabled={loading || datasPaisHora.length === 0}
                            >
                                <InputLabel>Mín. Pães Hora</InputLabel>
                                <Select
                                    value={filtroMinimo}
                                    label="Mín. Pães Hora"
                                    onChange={(e) => handleFiltroChange(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Todos</em>
                                    </MenuItem>
                                    {numerosDisponiveis.map((num) => (
                                        <MenuItem key={num} value={num}>
                                            ≥ {num}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
                
                <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Selecione até 4 datas Pães Hora
                        </Typography>
                        <Autocomplete
                            sx={{
                                width: '100%',
                                maxWidth: 400,
                                minWidth: 400,
                                maxHeight: 95,
                                minHeight: 95,
                                '& .MuiInputBase-root': {
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    maxHeight: '95px',
                                    minHeight: '95px',
                                    overflowY: 'hidden',
                                    overflowX: 'hidden',
                                    alignItems: 'flex-start',
                                    whiteSpace: 'normal',
                                },
                                '& .MuiAutocomplete-input': {
                                    flexGrow: 1,
                                    minWidth: '50px',
                                }
                            }}
                            limitTags={4}
                            multiple
                            // usa a lista filtrada; exclui os já selecionados
                            options={datasPaisHoraFiltradas.filter(data => !selectedPaesHora.includes(data))}
                            value={selectedPaesHora}
                            onChange={(event, newValue) => {
                                if (newValue.length <= 4) {
                                    setSelectedPaesHora(newValue);
                                }
                            }}
                            getOptionDisabled={(option) =>
                                selectedPaesHora.length >= 4 && !selectedPaesHora.includes(option)
                            }
                            onOpen={() => setOpenPaesHora(true)}
                            onClose={(event, reason) => {
                                if (reason !== 'blur' && reason !== 'escape') {
                                    return;
                                }
                                setOpenPaesHora(false);
                            }}
                            open={openPaesHora}
                            renderInput={(params) => <TextField {...params} />}
                            disabled={loading}
                        />
                    </Grid>
                    
                    <Grid xs={12} md={6}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Selecione até 4 datas Mix
                        </Typography>
                        <Autocomplete
                            sx={{
                                width: '100%',
                                maxWidth: 400,
                                minWidth: 400,
                                maxHeight: 95,
                                minHeight: 95,
                                '& .MuiInputBase-root': {
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    maxHeight: '95px',
                                    minHeight: '95px',
                                    overflowY: 'hidden',
                                    overflowX: 'hidden',
                                    alignItems: 'flex-start',
                                    whiteSpace: 'normal',
                                },
                                '& .MuiAutocomplete-input': {
                                    flexGrow: 1,
                                    minWidth: '50px',
                                }
                            }}  
                            limitTags={4}          
                            multiple
                            options={datasMix.filter(data => !selectedMix.includes(data))}
                            value={selectedMix}
                            onChange={(event, newValue) => {
                                if (newValue.length <= 4) {
                                    setSelectedMix(newValue);
                                }
                            }}
                            getOptionDisabled={(option) =>
                                selectedMix.length >= 4 && !selectedMix.includes(option)
                            }
                            onOpen={() => setOpenMix(true)}
                            onClose={(event, reason) => {
                                if (reason !== 'blur' && reason !== 'escape') {
                                    return;
                                }
                                setOpenMix(false);
                            }}
                            open={openMix}
                            renderInput={(params) => <TextField {...params} />}
                            disabled={loading}
                        />
                    </Grid>
                </Grid>
            </Box>
            
            {loading && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Carregando dados...
                </Typography>
            )}
            {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {colunasHora.map((col) => (
                                        <TableCell key={col} sx={{ fontWeight: 'bold' }}>
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tabelaPaisHoraData.length > 0 ? (
                                    tabelaPaisHoraData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{row.hora}</TableCell>
                                            {row.dados.map((dado, idx) => (
                                                <TableCell key={idx}>{dado}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={colunasHora.length}>Selecione datas para exibir os dados.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
    
                <Grid xs={12} md={6}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {colunasMix.map((col) => (
                                        <TableCell key={col} sx={{ fontWeight: 'bold' }}>
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tabelaMixData.length > 0 ? (
                                    tabelaMixData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{row.produto}</TableCell>
                                            {row.dados.map((dado, idx) => (
                                                <TableCell key={idx}>{dado}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={colunasMix.length}>Selecione datas para exibir os dados.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default GerarPlanilhaProducao;